import { useEffect } from 'react';
import { exportConfig, exportIds } from '../../config/config';
import { mountExportRoot, unmountExportRoot } from './exportRoot';

const INITIAL_DELAY_MS = 800;
const DOM_CHECK_INTERVAL_MS = 200;
const IMAGE_LOAD_TIMEOUT_MS = 8000;
const MAX_WAIT_MS = 15000;

function buildExportUrl(
  currentPathname: string,
  currentSearch: string,
  totalProducts: number
): string {
  const params = new URLSearchParams(currentSearch);
  params.set('page', '1');
  params.set(
    'page_size',
    String(Math.max(1, Math.min(totalProducts, exportConfig.maxProducts)))
  );
  const search = params.toString();
  return search ? `${currentPathname}?${search}` : currentPathname;
}

async function waitForContent(container: HTMLElement): Promise<void> {
  const start = Date.now();

  while (Date.now() - start < MAX_WAIT_MS) {
    const productCards = container.querySelectorAll('.ant-card');
    if (productCards.length > 0) {
      const images = container.querySelectorAll('img');
      const imagePromises = Array.from(images).map((img) => {
        if (img.complete && img.naturalHeight > 0) return Promise.resolve();
        return new Promise<void>((resolve) => {
          img.onload = () => resolve();
          img.onerror = () => resolve();
          setTimeout(resolve, IMAGE_LOAD_TIMEOUT_MS);
        });
      });
      await Promise.all(imagePromises);
      return;
    }
    await new Promise((r) => setTimeout(r, DOM_CHECK_INTERVAL_MS));
  }
}

interface ExportPdfWrapperProps {
  currentPathname: string;
  currentSearch: string;
  totalProducts: number;
  onReady: () => void;
}

export const ExportPdfWrapper = ({
  currentPathname,
  currentSearch,
  totalProducts,
  onReady,
}: ExportPdfWrapperProps) => {
  useEffect(() => {
    const exportUrl = buildExportUrl(
      currentPathname,
      currentSearch,
      totalProducts
    );
    mountExportRoot(exportUrl);

    const timer = setTimeout(() => {
      const root = document.getElementById(exportIds.pdfExportRoot);
      if (!root) {
        onReady();
        return;
      }
      waitForContent(root).then(() => {
        requestAnimationFrame(() => {
          requestAnimationFrame(onReady);
        });
      });
    }, INITIAL_DELAY_MS);

    const fallbackTimer = setTimeout(onReady, MAX_WAIT_MS);

    return () => {
      clearTimeout(timer);
      clearTimeout(fallbackTimer);
      unmountExportRoot();
    };
  }, []);

  return null;
};
